/**
 * Water surface shader, GLSL (WebGL2) and WGSL (WebGPU) with the same logic line for line.
 *
 * Inputs (see waterSystem.ts for who sets what):
 * - Surface motion: a generated tiling detail texture (waterNormalMap.ts) sampled in 2 or 4
 *   scrolling layers at different scales, angles and speeds, plus 2 or 4 "steep sine" swell waves
 *   (Gerstner-like sharp crests, shading only: the surface itself stays flat so the shoreline
 *   never moves). The two are summed as slopes and turned into a normal.
 * - Reflection: Fresnel (Schlick, F0 0.02) scaled by reflectivity, of a planar reflection of the
 *   scene (WATER_PLANAR, a reflection camera like the mirrors') composited over a sky gradient.
 * - What is under the surface: the splats' linear depth (SplatDepthPass, WATER_DEPTH, bound as
 *   uWaterDepthMap: while the splat draw is split at the water, waterSplatSplit.ts, it holds only
 *   the splats behind the water, since those in front draw over it) gives the
 *   length of the view ray inside the water at every pixel. While a model stands in the water
 *   the scene depth map (the engine's depth grab, which holds the opaque models) is read too
 *   (uWaterMeshDepth.x), so a submerged part of a model is the bottom where it is in front of
 *   the splats. That drives Beer-Lambert absorption
 *   (shallow tint near, deep colour far), shore fading and foam where the bottom comes close to
 *   the surface, and hides the water behind splats that stand in front of it (splats write no
 *   ordinary depth). With WATER_REFRACTION a copy of the frame is sampled at an offset bent by
 *   the waves (and the straight sample kept where the bent one would pick up something in front
 *   of the water); without it the water alpha-blends over the splats.
 * - Sun: a sharp glint plus a broad sheen from the scene's directional light (or a set direction).
 * - Caustics: two crossing layers of the texture's ridged channel projected onto the bottom.
 *
 * Output follows the gsplat convention (tone map, gamma) so water sits in the same colour space
 * as the splats on every pipeline (backbuffer, CameraFrame HDR target). Premultiplied alpha.
 */

import { MESH_CLOUD_SHADOW_CORE_GLSL, MESH_CLOUD_SHADOW_CORE_WGSL } from '../clouds/cloudMeshShadows';

/**
 * WATER_CLOUD_SHADOW (set while clouds cast shadows, see clouds/cloudMeshShadows.ts): the sun's
 * glint and sheen dim under a cloud. Not a tier define: the tiers never clear it.
 */
export const WATER_CLOUD_SHADOW_DEFINE = 'WATER_CLOUD_SHADOW';

/** Material defines per tier: see waterParams.selectWaterQuality. */
export const WATER_DEFINES = ['WATER_HIGH', 'WATER_DEPTH', 'WATER_REFRACTION', 'WATER_PLANAR', 'WATER_OPAQUE'] as const;
export type WaterDefine = (typeof WATER_DEFINES)[number];

export const WATER_VS_GLSL = /* glsl */ `
attribute vec3 aPosition;
uniform mat4 matrix_model;
uniform mat4 matrix_viewProjection;
varying vec3 vWorldPos;
varying vec4 vClipPos;
void main(void) {
    vec4 worldPos = matrix_model * vec4(aPosition, 1.0);
    vWorldPos = worldPos.xyz;
    gl_Position = matrix_viewProjection * worldPos;
    vClipPos = gl_Position;
}
`;

export const WATER_FS_GLSL = /* glsl */ `
#include "decodePS"
#include "gammaPS"
#include "tonemappingPS"
#include "fogPS"

varying vec3 vWorldPos;
varying vec4 vClipPos;

uniform float uTime;
uniform vec3 uCameraPos;
uniform vec3 uCameraForward;
uniform vec3 uPlaneNormal;
uniform vec3 uPlaneTangent;
uniform vec3 uPlaneBitangent;
// x: scene units per world unit, y: water size (scene units), z: visibility distance (scene units), w: shore distance (scene units)
uniform vec4 uMetrics;
// x: detail texture size, y: max transmission (clarity), z: caustic frequency, w: reflection distortion
uniform vec4 uMetrics2;
// world units per pixel at unit distance (2 tan(fov/2) / height)
uniform float uPixel;
#ifdef WATER_DEBUG
uniform float uDebugMode;
#endif
uniform vec3 uShallowColor;
uniform vec3 uDeepColor;
// x: strength, y: frequency (tiles per scene unit), z: direction (radians), w: 1 when still
uniform vec4 uWave;
uniform vec4 uSwell0;
uniform vec4 uSwell1;
uniform vec4 uSwell2;
uniform vec4 uSwell3;
// x: reflectivity, y: refraction, z: caustics, w: foam
uniform vec4 uLook;
// x: sun glint, y: stylised (0/1), z: sun visibility (0..1), w: unused
uniform vec4 uLook2;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyZenith;
uniform vec3 uSkyHorizon;
#ifdef WATER_CLOUD_SHADOW
${MESH_CLOUD_SHADOW_CORE_GLSL}
#endif
uniform sampler2D uWaterDetail;

#ifdef WATER_PLANAR
uniform sampler2D uReflectionMap;
#endif
#ifdef WATER_REFRACTION
uniform sampler2D uSceneColorMap;
#endif
#ifdef WATER_DEPTH
uniform vec4 uWaterDepthParams;
uniform highp sampler2D uWaterDepthMap;
uniform vec4 viewport_size;
float splatDepthTexel(ivec2 texel) {
    vec4 data = texelFetch(uWaterDepthMap, texel, 0);
    #ifdef CAPS_TEXTURE_FLOAT_RENDERABLE
        return data.r;
    #else
        uint bits = (uint(data.r * 255.0) << 24u) | (uint(data.g * 255.0) << 16u) | (uint(data.b * 255.0) << 8u) | uint(data.a * 255.0);
        return uintBitsToFloat(bits);
    #endif
}
// Nearest splat in a 2x2 block: closes the one-texel gaps between splats (they read as "nothing
// behind the water", dark speckles in shallow water) at the cost of growing occluders by a texel.
float splatDepthAt(vec2 uv) {
    ivec2 size = textureSize(uWaterDepthMap, 0);
    ivec2 texel = clamp(ivec2(uv * vec2(size)), ivec2(0), size - 2);
    float d = min(splatDepthTexel(texel), splatDepthTexel(texel + ivec2(1, 0)));
    return min(d, min(splatDepthTexel(texel + ivec2(0, 1)), splatDepthTexel(texel + ivec2(1, 1))));
}
#include "screenDepthPS"
// x: 1 while the scene depth map holds the opaque models (a model stands in this water)
uniform vec4 uWaterMeshDepth;
// The opaque models' linear depth at uv, or far away while the map is not taken.
float meshDepthAt(vec2 uv) {
    return uWaterMeshDepth.x > 0.5 ? getLinearScreenDepth(uv) : 1e6;
}
#endif

vec2 rot2(vec2 p, float a) {
    float s = sin(a);
    float c = cos(a);
    return vec2(c * p.x - s * p.y, s * p.x + c * p.y);
}

// One scrolling layer of the detail texture: (slope.x, slope.y, height, ridge) in plane space.
// 'pix' is the pixel size in scene units: a layer whose ripples would be only a few pixels
// long fades out instead of shimmering (normal-map LOD).
vec4 detailLayer(vec2 p, float freq, float angle, float speed, float pix) {
    vec2 q = rot2(p, -angle) * freq;
    q.x -= uTime * speed;
    vec4 s = texture2D(uWaterDetail, q);
    float lod = smoothstep(1.5, 5.0, 1.0 / max(freq * 8.0 * pix, 1e-5));
    vec2 slope = rot2(s.rg * 2.0 - 1.0, angle) * lod;
    return vec4(slope, mix(0.5, s.b, lod), s.a);
}

// Steep-sine swell wave (Gerstner-like sharp crests): slope in plane space. w = (kx, ky, omega, steepness).
vec2 swellSlope(vec2 p, vec4 w) {
    float theta = dot(w.xy, p) - w.z * uTime;
    float s = sin(theta) * 0.5 + 0.5;
    float k = length(w.xy);
    return w.w * 2.5 * pow(s, 1.5) * cos(theta) * (w.xy / max(k, 1e-5));
}

vec3 skyColor(vec3 dir, vec3 up) {
    float e = clamp(dot(dir, up), 0.0, 1.0);
    return mix(uSkyHorizon, uSkyZenith, pow(e, 0.45));
}

void main(void) {
    float invUnit = uMetrics.x;
    vec3 toFrag = vWorldPos - uCameraPos;
    float dist = length(toFrag);
    vec3 V = -toFrag / max(dist, 1e-6);
    float side = dot(V, uPlaneNormal) >= 0.0 ? 1.0 : -1.0;
    vec3 N0 = uPlaneNormal * side;
    float cosV = max(dot(V, N0), 0.0);
    vec2 p = vec2(dot(vWorldPos, uPlaneTangent), dot(vWorldPos, uPlaneBitangent)) * invUnit;
    float distScene = dist * invUnit;

    // ── surface motion ──
    float freq = uWave.y;
    float dirA = uWave.z;
    float strength = uWave.x;
    float pix = distScene * uPixel / max(sqrt(cosV), 0.25);
    vec4 d1 = detailLayer(p, freq * 0.5, dirA, 0.045, pix);
    vec4 d2 = detailLayer(p, freq * 0.83, dirA + 2.1, 0.06, pix);
    // slopeLow (swell + broad ripples) bends what is seen through and in the water; the fine
    // layers only shade, so refraction and reflections wobble smoothly instead of fizzing.
    vec2 slopeLow = d1.xy * 0.55 + d2.xy * 0.45;
    vec2 slope = slopeLow;
    float height = d1.z * 0.6 + d2.z * 0.4;
    // two ridged layers drifting apart: a moving web for foam lace
    float lace = d1.w * d2.w;
    #ifdef WATER_HIGH
        vec4 d3 = detailLayer(p, freq * 1.93, dirA - 1.3, 0.11, pix);
        vec4 d4 = detailLayer(p, freq * 3.71, dirA + 0.6, 0.16, pix);
        slope = slope * 0.75 + d3.xy * 0.3 + d4.xy * 0.2;
        height = height * 0.8 + d3.z * 0.2;
    #endif
    // fade fine detail with distance (less shimmer) and toward grazing angles
    float farF = smoothstep(4.0, 60.0, distScene * freq);
    float detailGain = mix(1.0, 0.45, farF) * mix(0.45, 1.0, sqrt(cosV)) * 0.42 * strength;
    slope *= detailGain;
    slopeLow *= detailGain;
    vec2 swell = swellSlope(p, uSwell0) + swellSlope(p, uSwell1);
    #ifdef WATER_HIGH
        swell += swellSlope(p, uSwell2) + swellSlope(p, uSwell3);
    #endif
    swell *= mix(1.0, 0.6, farF);
    slope += swell;
    slopeLow += swell;
    vec3 N = normalize(N0 - side * (slope.x * uPlaneTangent + slope.y * uPlaneBitangent));

    // ── reflection ──
    vec3 R = reflect(-V, N);
    vec3 reflection = skyColor(R, N0);
    #ifdef WATER_PLANAR
        vec2 ruv = vClipPos.xy / vClipPos.w * 0.5 + 0.5;
        ruv.x = 1.0 - ruv.x;
        ruv += slopeLow * uMetrics2.w;
        vec4 rs = textureLod(uReflectionMap, clamp(ruv, vec2(0.001), vec2(0.999)), 0.0);
        reflection = decodeGamma(rs.rgb) + reflection * (1.0 - rs.a);
    #endif
    float NdotV = clamp(dot(N, V), 0.0, 1.0);
    // Schlick Fresnel; reflectivity 0.5 is water's own (F0 about 0.02-0.03), 1 a mirror-like sheen.
    float F0 = 0.06 * uLook.x;
    float F = clamp(F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0) * uLook.x * 2.0, 0.0, 1.0);
    if (side < 0.0) { F = 0.0; }

    // ── sun ──
    vec3 L = normalize(uSunDir);
    vec3 H = normalize(V + L);
    float NdotH = max(dot(N, H), 0.0);
    float glint = pow(NdotH, 2000.0) * 5.0 + pow(NdotH, 120.0) * 0.12;
    if (uLook2.y > 0.5) {
        glint = step(0.993, NdotH) * 1.6 + step(0.95, NdotH) * 0.12;
    }
    vec3 sunLight = uSunColor * glint * uLook2.x * uLook2.z * max(side, 0.0);
    #ifdef WATER_CLOUD_SHADOW
        sunLight *= meshCloudShadowFactor(vWorldPos);
    #endif

    // ── what is under the surface ──
    float viewDepth = dot(toFrag, uCameraForward);
    float rayPerDepth = dist / max(viewDepth, 1e-5);
    float pathLen = 1e6;
    float vertDepth = 1e6;
    float coverage = 1.0;   // 0 where a splat stands in front of the water
    float shoreFade = 1.0;  // the water body thinning out at the shore (foam stays)
    float bottomDepth = viewDepth + 1e6;
    vec3 transmitted = vec3(0.0);
    #ifdef WATER_DEPTH
        vec2 screenUv = gl_FragCoord.xy * viewport_size.zw;
        float sceneDepth = splatDepthAt(screenUv);
        // a model behind the surface here is the bottom (one in front hides this pixel already)
        float meshDepth = meshDepthAt(screenUv);
        if (meshDepth > viewDepth) sceneDepth = min(sceneDepth, meshDepth);
        bool valid = uWaterDepthParams.x > 0.5;
        float dz = valid ? sceneDepth - viewDepth : 1e6;
        float above = max(-dz, 0.0) * rayPerDepth * invUnit * cosV;
        float tol = uMetrics.y * 0.004 + uMetrics.w * 0.25 + 1e-4;
        coverage = valid ? 1.0 - smoothstep(tol, tol * 2.0, above) : 1.0;
        bottomDepth = valid ? sceneDepth : bottomDepth;
        #ifdef WATER_REFRACTION
            float shallowRefr = clamp(max(dz, 0.0) * rayPerDepth * invUnit * cosV / max(uMetrics.y * 0.04, 1e-4), 0.0, 1.0);
            vec2 uvTry = screenUv + slopeLow * uLook.y * 0.06 * shallowRefr;
            // a model in front of the water there must not be pulled into the refraction either
            float dTry = min(splatDepthAt(uvTry), meshDepthAt(uvTry));
            // A map of only the splats behind the water (w: its empty value, waterSplatSplit.ts) is
            // empty where only splats in front of it are; the frame copy has nothing there either.
            bool known = uWaterDepthParams.w <= 0.0 || dTry < uWaterDepthParams.w * 0.999;
            float useTry = valid && dTry > viewDepth && known ? 1.0 : 0.0;
            vec2 uvR = mix(screenUv, uvTry, useTry);
            bottomDepth = valid ? mix(sceneDepth, dTry, useTry) : bottomDepth;
            float lod = (1.0 - uMetrics2.y) * 2.5;
            transmitted = textureLod(uSceneColorMap, uvR, lod).rgb;
            #ifdef SCENE_COLORMAP_GAMMA
                transmitted = decodeGamma(transmitted);
            #endif
        #endif
        pathLen = max(bottomDepth - viewDepth, 0.0) * rayPerDepth * invUnit;
        vertDepth = pathLen * cosV;
        if (uMetrics.w > 0.0) {
            shoreFade = smoothstep(0.0, uMetrics.w, vertDepth);
        }
    #endif

    // absorption (Beer-Lambert) and the light scattered back by the water body
    float sigma = 3.0 / max(uMetrics.z, 1e-4);
    float T = exp(-pathLen * sigma) * uMetrics2.y;
    float depthMix = 1.0 - exp(-pathLen * sigma * 0.6);
    // deep water keeps a little of the shallow tint (in-scattering), so it never goes flat black
    vec3 scatter = mix(uShallowColor, uDeepColor, depthMix * 0.85);
    #ifdef WATER_OPAQUE
        T = 0.0;
        scatter = mix(uShallowColor, uDeepColor, 0.55 + 0.25 * (height - 0.5));
    #endif
    float sunUp = clamp(uSunDir.y, 0.0, 1.0);
    scatter *= 0.85 + 0.25 * sunUp;
    if (uLook2.y > 0.5) {
        float bands = depthMix * 3.0;
        scatter = mix(uShallowColor, uDeepColor, (floor(bands) + smoothstep(0.8, 1.0, fract(bands))) / 3.0);
    }

    // caustics on the bottom, strongest in shallow water
    float caustic = 0.0;
    #ifdef WATER_DEPTH
        vec3 bottom = uCameraPos + (toFrag / max(dist, 1e-6)) * (min(bottomDepth, viewDepth + uMetrics.z * 4.0 / max(invUnit, 1e-6)) * rayPerDepth);
        // Planar coordinates leaned by height: walls get the pattern too instead of streaks.
        vec2 pb = vec2(dot(bottom, uPlaneTangent + uPlaneNormal * 0.6), dot(bottom, uPlaneBitangent - uPlaneNormal * 0.6)) * invUnit;
        float footprint = viewDepth * invUnit * 1.2 * viewport_size.w;
        float lodC = clamp(log2(max(footprint * uMetrics2.z * uMetrics2.x, 1.0)), 0.0, 7.0);
        float c1 = textureLod(uWaterDetail, pb * uMetrics2.z + vec2(uTime * 0.031, uTime * 0.017), lodC).a;
        float c2 = textureLod(uWaterDetail, rot2(pb, 1.7) * uMetrics2.z * 1.31 - vec2(uTime * 0.023, -uTime * 0.029), lodC).a;
        caustic = pow(clamp(c1 * c2 * 1.25, 0.0, 1.0), 7.0) * 3.0;
        caustic *= uLook.z * exp(-vertDepth * sigma * 0.5) * (0.4 + 0.6 * sunUp) * mix(1.0, 0.3, lodC / 7.0);
        if (uLook2.y > 0.5) { caustic = step(0.55, caustic) * 0.8 * uLook.z; }
    #endif

    // foam: along the shore (needs depth) and on crests
    // foam: a lacy band along the shore (needs depth) and whitecaps on crests
    float shoreFoam = 0.0;
    #ifdef WATER_DEPTH
        // a thin line hugging the waterline, and lacy patches a little further out
        float foamDist = max(uMetrics.y * 0.012 * (0.5 + uLook.w), 1e-4);
        float edge = 1.0 - smoothstep(0.0, foamDist, vertDepth);
        float line = 1.0 - smoothstep(0.0, foamDist * 0.3, vertDepth);
        shoreFoam = max(line * (0.55 + 0.45 * lace), edge * smoothstep(0.42, 0.62, lace));
        if (uLook2.y > 0.5) {
            // toon shoreline: a solid line plus a second one washing in and out
            float band = fract(vertDepth / foamDist * 0.6 - uTime * 0.2);
            shoreFoam = max(step(0.55, edge), step(0.82, band) * (1.0 - smoothstep(foamDist, foamDist * 3.0, vertDepth)));
        }
    #endif
    float crest = smoothstep(0.6, 0.85, height) * smoothstep(0.6, 1.6, strength) * smoothstep(0.3, 0.7, lace);
    // far away the lace is mipped to a flat grey: thin the foam out instead of painting a band
    float foamDetail = smoothstep(1.5, 5.0, 1.0 / max(freq * 4.0 * pix, 1e-5));
    float foam = clamp((shoreFoam + crest * 0.8) * min(uLook.w * 1.5, 1.0), 0.0, 1.0) * mix(0.3, 1.0, foamDetail);
    if (uLook2.y > 0.5) { foam = step(0.5, foam); }
    vec3 foamColor = vec3(0.9, 0.95, 1.0) * (0.55 + 0.45 * sunUp);

    // ── compose: scene-light part (tone mapped) + transmitted part (already in output space) ──
    vec3 light = (1.0 - F) * scatter * (1.0 - T) + F * reflection + sunLight;
    light = mix(light, foamColor, foam);
    vec3 tint = mix(vec3(1.0), uShallowColor / max(max(uShallowColor.r, uShallowColor.g), max(uShallowColor.b, 1e-4)), 0.45);
    vec3 color;
    float alpha;
    // The shore fade thins the water body; foam stays on top of it.
    float bodyAlpha = mix(foam, 1.0, shoreFade);
    #ifdef WATER_REFRACTION
        vec3 trans = (1.0 - F) * T * transmitted * tint * (1.0 + caustic) * (1.0 - foam);
        color = toneMap(addFog(light)) + trans;
        alpha = 1.0;
    #else
        light += (1.0 - F) * T * caustic * uSunColor * 0.35 * tint;
        color = toneMap(addFog(light));
        alpha = clamp(F + (1.0 - F) * (1.0 - T), 0.0, 1.0);
        alpha = max(alpha, foam);
        #ifdef WATER_OPAQUE
            alpha = 1.0;
        #endif
    #endif
    color = gammaCorrectOutput(max(color, vec3(0.0)));
    float a = coverage * bodyAlpha;
    gl_FragColor = vec4(color * a, alpha * a);
    #ifdef WATER_DEBUG
        // verify-water.mjs: inspect the inputs (1: coverage / shore / transmittance, 2: depths, 3: reflection)
        if (uDebugMode < 1.5) gl_FragColor = vec4(coverage, shoreFade, T, 1.0);
        #ifdef WATER_DEPTH
            else if (uDebugMode < 2.5) gl_FragColor = vec4(fract(sceneDepth), fract(viewDepth), valid ? 1.0 : 0.0, 1.0);
        #endif
        else gl_FragColor = vec4(reflection, 1.0);
    #endif
}
`;

export const WATER_VS_WGSL = /* wgsl */ `
attribute aPosition: vec3f;
uniform matrix_model: mat4x4f;
uniform matrix_viewProjection: mat4x4f;
varying vWorldPos: vec3f;
varying vClipPos: vec4f;
@vertex
fn vertexMain(input: VertexInput) -> VertexOutput {
    var output: VertexOutput;
    let worldPos: vec4f = uniform.matrix_model * vec4f(aPosition, 1.0);
    output.vWorldPos = worldPos.xyz;
    output.position = uniform.matrix_viewProjection * worldPos;
    output.vClipPos = output.position;
    return output;
}
`;

export const WATER_FS_WGSL = /* wgsl */ `
#include "decodePS"
#include "gammaPS"
#include "tonemappingPS"
#include "fogPS"

varying vWorldPos: vec3f;
varying vClipPos: vec4f;

uniform uTime: f32;
uniform uCameraPos: vec3f;
uniform uCameraForward: vec3f;
uniform uPlaneNormal: vec3f;
uniform uPlaneTangent: vec3f;
uniform uPlaneBitangent: vec3f;
uniform uMetrics: vec4f;
uniform uMetrics2: vec4f;
uniform uPixel: f32;
#ifdef WATER_DEBUG
uniform uDebugMode: f32;
#endif
uniform uShallowColor: vec3f;
uniform uDeepColor: vec3f;
uniform uWave: vec4f;
uniform uSwell0: vec4f;
uniform uSwell1: vec4f;
uniform uSwell2: vec4f;
uniform uSwell3: vec4f;
uniform uLook: vec4f;
uniform uLook2: vec4f;
uniform uSunDir: vec3f;
uniform uSunColor: vec3f;
uniform uSkyZenith: vec3f;
uniform uSkyHorizon: vec3f;
#ifdef WATER_CLOUD_SHADOW
${MESH_CLOUD_SHADOW_CORE_WGSL}
#endif
var uWaterDetail: texture_2d<f32>;
var uWaterDetailSampler: sampler;

#ifdef WATER_PLANAR
var uReflectionMap: texture_2d<f32>;
var uReflectionMapSampler: sampler;
#endif
#ifdef WATER_REFRACTION
var uSceneColorMap: texture_2d<f32>;
var uSceneColorMapSampler: sampler;
#endif
#ifdef WATER_DEPTH
uniform uWaterDepthParams: vec4f;
var uWaterDepthMap: texture_2d<uff>;
uniform viewport_size: vec4f;
fn splatDepthAt(uv: vec2f) -> f32 {
    let size = vec2i(textureDimensions(uWaterDepthMap, 0));
    let texel = clamp(vec2i(uv * vec2f(size)), vec2i(0), size - vec2i(2));
    let d = min(textureLoad(uWaterDepthMap, texel, 0).r, textureLoad(uWaterDepthMap, texel + vec2i(1, 0), 0).r);
    return min(d, min(textureLoad(uWaterDepthMap, texel + vec2i(0, 1), 0).r, textureLoad(uWaterDepthMap, texel + vec2i(1, 1), 0).r));
}
#include "screenDepthPS"
// x: 1 while the scene depth map holds the opaque models (a model stands in this water)
uniform uWaterMeshDepth: vec4f;
// The opaque models' linear depth at uv, or far away while the map is not taken.
fn meshDepthAt(uv: vec2f) -> f32 {
    if (uniform.uWaterMeshDepth.x > 0.5) { return getLinearScreenDepth(uv); }
    return 1e6;
}
#endif

fn rot2(p: vec2f, a: f32) -> vec2f {
    let s = sin(a);
    let c = cos(a);
    return vec2f(c * p.x - s * p.y, s * p.x + c * p.y);
}

fn detailLayer(p: vec2f, freq: f32, angle: f32, speed: f32, pix: f32) -> vec4f {
    var q = rot2(p, -angle) * freq;
    q.x = q.x - uniform.uTime * speed;
    let s = textureSample(uWaterDetail, uWaterDetailSampler, q);
    let lod = smoothstep(1.5, 5.0, 1.0 / max(freq * 8.0 * pix, 1e-5));
    let slope = rot2(s.rg * 2.0 - 1.0, angle) * lod;
    return vec4f(slope, mix(0.5, s.b, lod), s.a);
}

fn swellSlope(p: vec2f, w: vec4f) -> vec2f {
    let theta = dot(w.xy, p) - w.z * uniform.uTime;
    let s = sin(theta) * 0.5 + 0.5;
    let k = length(w.xy);
    return w.w * 2.5 * pow(s, 1.5) * cos(theta) * (w.xy / max(k, 1e-5));
}

fn skyColor(dir: vec3f, up: vec3f) -> vec3f {
    let e = clamp(dot(dir, up), 0.0, 1.0);
    return mix(uniform.uSkyHorizon, uniform.uSkyZenith, vec3f(pow(e, 0.45)));
}

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let invUnit = uniform.uMetrics.x;
    let toFrag = vWorldPos - uniform.uCameraPos;
    let dist = length(toFrag);
    let V = -toFrag / max(dist, 1e-6);
    let side = select(-1.0, 1.0, dot(V, uniform.uPlaneNormal) >= 0.0);
    let N0 = uniform.uPlaneNormal * side;
    let cosV = max(dot(V, N0), 0.0);
    let p = vec2f(dot(vWorldPos, uniform.uPlaneTangent), dot(vWorldPos, uniform.uPlaneBitangent)) * invUnit;
    let distScene = dist * invUnit;

    // surface motion
    let freq = uniform.uWave.y;
    let dirA = uniform.uWave.z;
    let strength = uniform.uWave.x;
    let pix = distScene * uniform.uPixel / max(sqrt(cosV), 0.25);
    let d1 = detailLayer(p, freq * 0.5, dirA, 0.045, pix);
    let d2 = detailLayer(p, freq * 0.83, dirA + 2.1, 0.06, pix);
    var slopeLow = d1.xy * 0.55 + d2.xy * 0.45;
    var slope = slopeLow;
    var height = d1.z * 0.6 + d2.z * 0.4;
    let lace = d1.w * d2.w;
    #ifdef WATER_HIGH
        let d3 = detailLayer(p, freq * 1.93, dirA - 1.3, 0.11, pix);
        let d4 = detailLayer(p, freq * 3.71, dirA + 0.6, 0.16, pix);
        slope = slope * 0.75 + d3.xy * 0.3 + d4.xy * 0.2;
        height = height * 0.8 + d3.z * 0.2;
    #endif
    let farF = smoothstep(4.0, 60.0, distScene * freq);
    let detailGain = mix(1.0, 0.45, farF) * mix(0.45, 1.0, sqrt(cosV)) * 0.42 * strength;
    slope = slope * detailGain;
    slopeLow = slopeLow * detailGain;
    var swell = swellSlope(p, uniform.uSwell0) + swellSlope(p, uniform.uSwell1);
    #ifdef WATER_HIGH
        swell = swell + swellSlope(p, uniform.uSwell2) + swellSlope(p, uniform.uSwell3);
    #endif
    swell = swell * mix(1.0, 0.6, farF);
    slope = slope + swell;
    slopeLow = slopeLow + swell;
    let N = normalize(N0 - side * (slope.x * uniform.uPlaneTangent + slope.y * uniform.uPlaneBitangent));

    // reflection
    let R = reflect(-V, N);
    var reflection = skyColor(R, N0);
    #ifdef WATER_PLANAR
        var ruv = vClipPos.xy / vClipPos.w * 0.5 + vec2f(0.5);
        ruv.x = 1.0 - ruv.x;
        ruv = ruv + slopeLow * uniform.uMetrics2.w;
        let rs = textureSampleLevel(uReflectionMap, uReflectionMapSampler, clamp(ruv, vec2f(0.001), vec2f(0.999)), 0.0);
        reflection = decodeGamma3(rs.rgb) + reflection * (1.0 - rs.a);
    #endif
    let NdotV = clamp(dot(N, V), 0.0, 1.0);
    let F0 = 0.06 * uniform.uLook.x;
    var F = clamp(F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0) * uniform.uLook.x * 2.0, 0.0, 1.0);
    if (side < 0.0) { F = 0.0; }

    // sun
    let L = normalize(uniform.uSunDir);
    let H = normalize(V + L);
    let NdotH = max(dot(N, H), 0.0);
    var glint = pow(NdotH, 2000.0) * 5.0 + pow(NdotH, 120.0) * 0.12;
    if (uniform.uLook2.y > 0.5) {
        glint = step(0.993, NdotH) * 1.6 + step(0.95, NdotH) * 0.12;
    }
    var sunLight = uniform.uSunColor * glint * uniform.uLook2.x * uniform.uLook2.z * max(side, 0.0);
    #ifdef WATER_CLOUD_SHADOW
        sunLight = sunLight * meshCloudShadowFactor(vWorldPos);
    #endif

    // what is under the surface
    let viewDepth = dot(toFrag, uniform.uCameraForward);
    let rayPerDepth = dist / max(viewDepth, 1e-5);
    var pathLen: f32 = 1e6;
    var vertDepth: f32 = 1e6;
    var coverage: f32 = 1.0;
    var shoreFade: f32 = 1.0;
    var bottomDepth: f32 = viewDepth + 1e6;
    var transmitted = vec3f(0.0);
    #ifdef WATER_DEPTH
        let screenUv = pcPosition.xy * uniform.viewport_size.zw;
        let splatDepth = splatDepthAt(screenUv);
        // a model behind the surface here is the bottom (one in front hides this pixel already)
        let meshDepth = meshDepthAt(screenUv);
        let sceneDepth = select(splatDepth, min(splatDepth, meshDepth), meshDepth > viewDepth);
        let valid = uniform.uWaterDepthParams.x > 0.5;
        let dz = select(1e6, sceneDepth - viewDepth, valid);
        let above = max(-dz, 0.0) * rayPerDepth * invUnit * cosV;
        let tol = uniform.uMetrics.y * 0.004 + uniform.uMetrics.w * 0.25 + 1e-4;
        coverage = select(1.0, 1.0 - smoothstep(tol, tol * 2.0, above), valid);
        bottomDepth = select(bottomDepth, sceneDepth, valid);
        #ifdef WATER_REFRACTION
            let shallowRefr = clamp(max(dz, 0.0) * rayPerDepth * invUnit * cosV / max(uniform.uMetrics.y * 0.04, 1e-4), 0.0, 1.0);
            let uvTry = screenUv + slopeLow * uniform.uLook.y * 0.06 * shallowRefr;
            let dTry = min(splatDepthAt(uvTry), meshDepthAt(uvTry));
            // only splats behind the water in the map (see the GLSL version)
            let known = uniform.uWaterDepthParams.w <= 0.0 || dTry < uniform.uWaterDepthParams.w * 0.999;
            let useTry = select(0.0, 1.0, valid && dTry > viewDepth && known);
            let uvR = mix(screenUv, uvTry, vec2f(useTry));
            bottomDepth = select(bottomDepth, mix(sceneDepth, dTry, useTry), valid);
            let lod = (1.0 - uniform.uMetrics2.y) * 2.5;
            transmitted = textureSampleLevel(uSceneColorMap, uSceneColorMapSampler, uvR, lod).rgb;
            #ifdef SCENE_COLORMAP_GAMMA
                transmitted = decodeGamma3(transmitted);
            #endif
        #endif
        pathLen = max(bottomDepth - viewDepth, 0.0) * rayPerDepth * invUnit;
        vertDepth = pathLen * cosV;
        if (uniform.uMetrics.w > 0.0) {
            shoreFade = smoothstep(0.0, uniform.uMetrics.w, vertDepth);
        }
    #endif

    let sigma = 3.0 / max(uniform.uMetrics.z, 1e-4);
    var T = exp(-pathLen * sigma) * uniform.uMetrics2.y;
    let depthMix = 1.0 - exp(-pathLen * sigma * 0.6);
    var scatter = mix(uniform.uShallowColor, uniform.uDeepColor, vec3f(depthMix * 0.85));
    #ifdef WATER_OPAQUE
        T = 0.0;
        scatter = mix(uniform.uShallowColor, uniform.uDeepColor, vec3f(0.55 + 0.25 * (height - 0.5)));
    #endif
    let sunUp = clamp(uniform.uSunDir.y, 0.0, 1.0);
    scatter = scatter * (0.85 + 0.25 * sunUp);
    if (uniform.uLook2.y > 0.5) {
        let bands = depthMix * 3.0;
        scatter = mix(uniform.uShallowColor, uniform.uDeepColor, vec3f((floor(bands) + smoothstep(0.8, 1.0, fract(bands))) / 3.0));
    }

    var caustic: f32 = 0.0;
    #ifdef WATER_DEPTH
        let bottom = uniform.uCameraPos + (toFrag / max(dist, 1e-6)) * (min(bottomDepth, viewDepth + uniform.uMetrics.z * 4.0 / max(invUnit, 1e-6)) * rayPerDepth);
        let pb = vec2f(dot(bottom, uniform.uPlaneTangent + uniform.uPlaneNormal * 0.6), dot(bottom, uniform.uPlaneBitangent - uniform.uPlaneNormal * 0.6)) * invUnit;
        let footprint = viewDepth * invUnit * 1.2 * uniform.viewport_size.w;
        let lodC = clamp(log2(max(footprint * uniform.uMetrics2.z * uniform.uMetrics2.x, 1.0)), 0.0, 7.0);
        let c1 = textureSampleLevel(uWaterDetail, uWaterDetailSampler, pb * uniform.uMetrics2.z + vec2f(uniform.uTime * 0.031, uniform.uTime * 0.017), lodC).a;
        let c2 = textureSampleLevel(uWaterDetail, uWaterDetailSampler, rot2(pb, 1.7) * uniform.uMetrics2.z * 1.31 - vec2f(uniform.uTime * 0.023, -uniform.uTime * 0.029), lodC).a;
        caustic = pow(clamp(c1 * c2 * 1.25, 0.0, 1.0), 7.0) * 3.0;
        caustic = caustic * uniform.uLook.z * exp(-vertDepth * sigma * 0.5) * (0.4 + 0.6 * sunUp) * mix(1.0, 0.3, lodC / 7.0);
        if (uniform.uLook2.y > 0.5) { caustic = step(0.55, caustic) * 0.8 * uniform.uLook.z; }
    #endif

    var shoreFoam: f32 = 0.0;
    #ifdef WATER_DEPTH
        let foamDist = max(uniform.uMetrics.y * 0.012 * (0.5 + uniform.uLook.w), 1e-4);
        let edge = 1.0 - smoothstep(0.0, foamDist, vertDepth);
        let line = 1.0 - smoothstep(0.0, foamDist * 0.3, vertDepth);
        shoreFoam = max(line * (0.55 + 0.45 * lace), edge * smoothstep(0.42, 0.62, lace));
        if (uniform.uLook2.y > 0.5) {
            let band = fract(vertDepth / foamDist * 0.6 - uniform.uTime * 0.2);
            shoreFoam = max(step(0.55, edge), step(0.82, band) * (1.0 - smoothstep(foamDist, foamDist * 3.0, vertDepth)));
        }
    #endif
    let crest = smoothstep(0.6, 0.85, height) * smoothstep(0.6, 1.6, strength) * smoothstep(0.3, 0.7, lace);
    let foamDetail = smoothstep(1.5, 5.0, 1.0 / max(freq * 4.0 * pix, 1e-5));
    var foam = clamp((shoreFoam + crest * 0.8) * min(uniform.uLook.w * 1.5, 1.0), 0.0, 1.0) * mix(0.3, 1.0, foamDetail);
    if (uniform.uLook2.y > 0.5) { foam = step(0.5, foam); }
    let foamColor = vec3f(0.9, 0.95, 1.0) * (0.55 + 0.45 * sunUp);

    var light = (1.0 - F) * scatter * (1.0 - T) + F * reflection + sunLight;
    light = mix(light, foamColor, vec3f(foam));
    let tint = mix(vec3f(1.0), uniform.uShallowColor / max(max(uniform.uShallowColor.r, uniform.uShallowColor.g), max(uniform.uShallowColor.b, 1e-4)), vec3f(0.45));
    let bodyAlpha = mix(foam, 1.0, shoreFade);
    var color: vec3f;
    var alpha: f32;
    #ifdef WATER_REFRACTION
        let trans = (1.0 - F) * T * transmitted * tint * (1.0 + caustic) * (1.0 - foam);
        color = toneMap(addFog(light)) + trans;
        alpha = 1.0;
    #else
        light = light + (1.0 - F) * T * caustic * uniform.uSunColor * 0.35 * tint;
        color = toneMap(addFog(light));
        alpha = clamp(F + (1.0 - F) * (1.0 - T), 0.0, 1.0);
        alpha = max(alpha, foam);
        #ifdef WATER_OPAQUE
            alpha = 1.0;
        #endif
    #endif
    color = gammaCorrectOutput(max(color, vec3f(0.0)));
    let a = coverage * bodyAlpha;
    output.color = vec4f(color * a, alpha * a);
    #ifdef WATER_DEBUG
        if (uniform.uDebugMode < 1.5) {
            output.color = vec4f(coverage, shoreFade, T, 1.0);
        } else if (uniform.uDebugMode < 2.5) {
            #ifdef WATER_DEPTH
                output.color = vec4f(fract(sceneDepth), fract(viewDepth), select(0.0, 1.0, valid), 1.0);
            #endif
        } else {
            output.color = vec4f(reflection, 1.0);
        }
    #endif
    return output;
}
`;
